import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { Postcard } from '../types';

function convertToDate(timestamp: unknown): Date | null {
  if (!timestamp) return null;
  if (timestamp instanceof Date) return timestamp;
  if (typeof timestamp === 'object' && timestamp !== null && 'seconds' in timestamp) {
    return new Date((timestamp as { seconds: number }).seconds * 1000);
  }
  if (typeof timestamp === 'object' && timestamp !== null && 'toDate' in timestamp && typeof (timestamp as { toDate: () => Date }).toDate === 'function') {
    return (timestamp as { toDate: () => Date }).toDate();
  }
  return new Date(String(timestamp));
}

export function PostcardViewPage() {
  const { id } = useParams();
  const [postcard, setPostcard] = useState<Postcard | null>(null);
  const [loading, setLoading] = useState(true);
  const [flipped, setFlipped] = useState(false);

  useEffect(() => {
    const load = async () => {
      if (!id) return;
      try {
        const ref = doc(db, 'postcards', id);
        const snap = await getDoc(ref);
        if (snap.exists()) {
          const data = snap.data() as Postcard;
          setPostcard({ ...data, id: snap.id });
        }
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!postcard) {
    return (
      <div className="flex items-center justify-center min-h-screen text-gray-600">
        Postcard not found.
      </div>
    );
  }

  const createdAt = convertToDate(postcard.createdAt)?.toLocaleDateString();

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-b from-gray-50 to-gray-100">
      <div className="w-[320px] sm:w-[360px] md:w-[400px]" onClick={() => setFlipped(!flipped)}>
        <div className="relative [perspective:1000px]">
          <div className={`relative h-[400px] sm:h-[450px] md:h-[500px] [transform-style:preserve-3d] transition-transform duration-500 ${flipped ? '[transform:rotateY(180deg)]' : ''}`}>
            {/* Front */}
            <div className="absolute inset-0 [backface-visibility:hidden] bg-white rounded-lg shadow-xl overflow-hidden">
              <div className="relative w-full h-full">
                <div className="absolute inset-0" style={{ aspectRatio: '4 / 5' }}>
                  <img src={postcard.imageURL} alt="Postcard" className="w-full h-full object-cover" />
                </div>
              </div>
            </div>
            {/* Back */}
            <div className="absolute inset-0 [backface-visibility:hidden] [transform:rotateY(180deg)] bg-white rounded-lg shadow-xl p-4 flex flex-col justify-between">
              <div>
                <div className="text-sm text-gray-500">{createdAt}</div>
                <div className="mt-2 text-gray-800 whitespace-pre-wrap">{postcard.message}</div>
              </div>
              <div className="text-sm text-gray-600 flex items-center justify-between">
                <span>{postcard.location}{postcard.country ? `, ${postcard.country}` : ''}</span>
                <span>Tap to flip</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PostcardViewPage;


